import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Card } from '@/console/features/card-layout/components/card/card';
import { TEmptyCard } from '@/console/features/card-layout/components/card/card.types';
import { GroupCardRow } from '@/console/features/card-layout/components/recent-groups-card/components/group-card-row/group-card-row';
import { useReadRootGroups } from '@/console/features/groups/hooks/use-read-root-groups/use-read-root-groups';
import { AddIcon } from '@/shared/assets/icons/add-icon';
import { FolderIcon } from '@/shared/assets/icons/folder-icon';
import { ShowIcon } from '@/shared/assets/icons/show-icon';
import { MenuItem } from '@/shared/components/ui/menu-item/menu-item';
import { CreateGroup } from '@/shared/features/groups/components/create-group/create-group';
import { useBackgroundRefetch } from '@/shared/hooks/use-background-refetch/use-background-refetch';
import {
  PaginationInput,
  ResourceGroupFragment,
} from '@/shared/types/types.generated';

export const RecentGroupsCard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pagination: PaginationInput = {
    page: 1,
    pageSize: 15,
  };
  const { data, isLoading, isFetching } = useReadRootGroups(pagination);
  const visibleIsFetching = useBackgroundRefetch(isFetching);
  const showLoader = isLoading || visibleIsFetching;

  const renderPopoverContent = () => {
    return (
      <>
        <MenuItem onClick={() => navigate('/groups')} icon={<ShowIcon />}>
          {t('card_layout:view_all_groups')}
        </MenuItem>
        <CreateGroup icon={<AddIcon />} label={t('card_layout:add_group')} />
      </>
    );
  };

  const getEmptyCardProps = (): TEmptyCard => {
    return {
      action: <CreateGroup icon={<AddIcon />} component="Button" />,
      icon: <FolderIcon />,
      text: t('card_layout:groups.empty_message'),
    };
  };

  const groups = data?.items;

  return (
    <Card<ResourceGroupFragment>
      title={t('card_layout:groups.title')}
      emptyCard={getEmptyCardProps()}
      data={groups}
      row={(group) => <GroupCardRow group={group} key={group.groupId} />}
      showLoader={showLoader}
      popoverContent={renderPopoverContent()}
    />
  );
};
